import {Grid} from '@material-ui/core';
import {SwitchCustom, TextBox} from '../../../Core/UI';
import React from 'react';
import {Parameter} from '../../../Core/mobx/ParameterStore/ParameterStore';
import {observer} from 'mobx-react-lite';
import {CategoryCard} from '../CategoryCard';
import {Approval} from '../../../Core/mobx/ParameterStore/Approval';
import {SelectInputElement} from '../../PreQualification/Components/SelectInputElement';
import {fieldOptions} from '../../../Core/mobx/ParameterStore/ApprovalXref';

type FamilyHistoryProps = {
    valueChanged: (p?: Parameter, value?: any) => void;
    parameters: Approval;
    setCategory: (entry: string) => void;
    index: number;
};

const FamilyHistory = observer(({valueChanged, parameters, setCategory, index}: FamilyHistoryProps) => {
    const required = (value?: string) => (
        <span>
            {value && value} <span style={{color: 'red'}}>*</span>
        </span>
    );

    const includesCondition = (condition: any) => {
        return (
            Array.isArray(parameters.biologicalFamilyCondition?.value) &&
            parameters.biologicalFamilyCondition.value.includes(condition)
        );
    };

    return (
        <CategoryCard title={'Family History'} setCategory={setCategory} index={index}>
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Is Your Father Still Living',
                        name: 'fatherLiving',
                        options: fieldOptions.fatherLiving.options,
                        ...parameters.fatherLiving,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.fatherLiving.value === 'Yes' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'fatherAge'}
                        label={required('Father Current Age')}
                        type="number"
                        value={parameters.fatherAge?.value}
                        onChange={(value) => valueChanged(parameters.fatherAge, value)}
                    />
                </Grid>
            )}
            {parameters.fatherLiving.value === 'No' && (
                <>
                    <Grid item xs={4}>
                        <TextBox
                            id={'fatherPassingAge'}
                            label={required('Father Age At Passing')}
                            type="number"
                            value={parameters.fatherPassingAge?.value}
                            onChange={(value) => valueChanged(parameters.fatherPassingAge, value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'fatherPassingCause'}
                            label={required('Father Cause Of Passing')}
                            value={parameters.fatherPassingCause?.value}
                            onChange={(value) => valueChanged(parameters.fatherPassingCause, value)}
                        />
                    </Grid>
                </>
            )}
            {parameters.fatherLiving.value === 'Unknown' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'fatherUnknown'}
                        label={required('Father Unknown Details')}
                        value={parameters.fatherUnknown?.value}
                        onChange={(value) => valueChanged(parameters.fatherUnknown, value)}
                    />
                </Grid>
            )}
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Is Your Mother Still Living',
                        name: 'motherLiving',
                        options: fieldOptions.motherLiving.options,
                        ...parameters.motherLiving,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                />
            </Grid>
            {parameters.motherLiving.value === 'Yes' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'motherAge'}
                        label={required('Mother Current Age')}
                        type="number"
                        value={parameters.motherAge?.value}
                        onChange={(value) => valueChanged(parameters.motherAge, value)}
                    />
                </Grid>
            )}
            {parameters.motherLiving.value === 'No' && (
                <>
                    <Grid item xs={4}>
                        <TextBox
                            id={'motherPassingAge'}
                            label={required('Mother Age At Passing')}
                            type="number"
                            value={parameters.motherPassingAge?.value}
                            onChange={(value) => valueChanged(parameters.motherPassingAge, value)}
                        />
                    </Grid>
                    <Grid item xs={4}>
                        <TextBox
                            id={'motherPassingCause'}
                            label={required('Mother Cause Of Passing')}
                            value={parameters.motherPassingCause?.value}
                            onChange={(value) => valueChanged(parameters.motherPassingCause, value)}
                        />
                    </Grid>
                </>
            )}
            {parameters.motherLiving.value === 'Unknown' && (
                <Grid item xs={4}>
                    <TextBox
                        id={'motherUnknown'}
                        label={required('Mother Unknown Details')}
                        value={parameters.motherUnknown?.value}
                        onChange={(value) => valueChanged(parameters.motherUnknown, value)}
                    />
                </Grid>
            )}
            <Grid item xs={4}>
                <SelectInputElement
                    param={{
                        label: 'Parent/Sibling Diagnosed',
                        name: 'biologicalFamilyCondition',
                        options: fieldOptions.biologicalFamilyCondition.options,
                        ...parameters.biologicalFamilyCondition,
                    }}
                    required={required('')}
                    valueChanged={valueChanged}
                    multiple
                />
            </Grid>
            {(includesCondition('Cancer') ||
                includesCondition('HeartDisease') ||
                includesCondition('Diabetes') ||
                includesCondition('Stroke')) && (
                <>
                    <Grid item xs={4}>
                        <SelectInputElement
                            param={{
                                label: 'Parent(s)/Sibling(s) With Diagnosis',
                                name: 'biologicalFamilyConditionWhichMember',
                                options: fieldOptions.biologicalFamilyConditionWhichMember.options,
                                ...parameters.biologicalFamilyConditionWhichMember,
                            }}
                            required={required('')}
                            valueChanged={valueChanged}
                            multiple
                        />
                    </Grid>
                    {['Father', 'Mother', 'Brother', 'Sister'].map(
                        (member) =>
                            parameters.biologicalFamilyConditionWhichMember.value?.includes(member) && (
                                <>
                                    <Grid item xs={4}>
                                        <SelectInputElement
                                            param={{
                                                label: `${member} Conditions`,
                                                name: `biologicalFamilyCondition${member}`,
                                                options: fieldOptions.biologicalFamilyConditionFather.options,
                                                ...parameters[
                                                    `biologicalFamilyCondition${member}` as keyof typeof parameters
                                                ],
                                            }}
                                            required={required('')}
                                            valueChanged={valueChanged}
                                            multiple
                                        />
                                    </Grid>
                                    {['Cancer', 'HeartDisease', 'Diabetes', 'Stroke'].map((disease) => {
                                        const ageParamName =
                                            disease === 'Cancer'
                                                ? `biologicalFamilyCondition${member}${disease}DiagnosedAge`
                                                : `biologicalFamilyCondition${member}${disease}Age`;

                                        const diedAgeParamName =
                                            disease === 'Cancer'
                                                ? `biologicalFamilyCondition${member}${disease}DiedAge`
                                                : `biologicalFamilyCondition${member}${disease}DiedAgee`;

                                        return (
                                            parameters[
                                                `biologicalFamilyCondition${member}` as keyof typeof parameters
                                            ]?.value.includes(disease) && (
                                                <>
                                                    <Grid item xs={4}>
                                                        <TextBox
                                                            id={parameters[ageParamName as keyof typeof parameters]}
                                                            label={required(`${member} Age Diagnosed With ${disease}`)}
                                                            type="number"
                                                            value={
                                                                parameters[ageParamName as keyof typeof parameters]
                                                                    .value
                                                            }
                                                            onChange={(value) =>
                                                                valueChanged(
                                                                    parameters[ageParamName as keyof typeof parameters],
                                                                    value,
                                                                )
                                                            }
                                                        />
                                                    </Grid>
                                                    <Grid item xs={4}>
                                                        <SwitchCustom
                                                            param={{
                                                                label: `${member} Died From ${disease}`,
                                                                name: `biologicalFamilyCondition${member}${disease}Died`,
                                                                ...parameters[
                                                                    `biologicalFamilyCondition${member}${disease}Died` as keyof typeof parameters
                                                                ],
                                                            }}
                                                            required={required('')}
                                                            valueChanged={valueChanged}
                                                        />
                                                    </Grid>
                                                    {parameters[
                                                        `biologicalFamilyCondition${member}${disease}Died` as keyof typeof parameters
                                                    ].value && (
                                                        <Grid item xs={4}>
                                                            <TextBox
                                                                id={
                                                                    parameters[
                                                                        diedAgeParamName as keyof typeof parameters
                                                                    ]
                                                                }
                                                                label={required(
                                                                    `${member} Age At Death From ${disease}`,
                                                                )}
                                                                type="number"
                                                                value={
                                                                    parameters[
                                                                        diedAgeParamName as keyof typeof parameters
                                                                    ].value
                                                                }
                                                                onChange={(value) =>
                                                                    valueChanged(
                                                                        parameters[
                                                                            diedAgeParamName as keyof typeof parameters
                                                                        ],
                                                                        value,
                                                                    )
                                                                }
                                                            />
                                                        </Grid>
                                                    )}
                                                    {disease === 'Cancer' && (
                                                        <Grid item xs={4}>
                                                            <SelectInputElement
                                                                param={{
                                                                    label: `${member} Cancer Type`,
                                                                    name: `biologicalFamilyCondition${member}CancerType`,
                                                                    options: ['Mother', 'Sister'].includes(member)
                                                                        ? fieldOptions
                                                                              .biologicalFamilyConditionMotherCancerType
                                                                              .options
                                                                        : fieldOptions
                                                                              .biologicalFamilyConditionFatherCancerType
                                                                              .options,
                                                                    ...parameters[
                                                                        `biologicalFamilyCondition${member}CancerType` as keyof typeof parameters
                                                                    ],
                                                                }}
                                                                required={required('')}
                                                                valueChanged={valueChanged}
                                                            />
                                                        </Grid>
                                                    )}
                                                </>
                                            )
                                        );
                                    })}
                                </>
                            ),
                    )}
                </>
            )}
        </CategoryCard>
    );
});

export default FamilyHistory;
